import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";  // CardHeader, CardTitleを削除
import { Badge } from "./ui/badge";
import { 
  Save, 
  RotateCw, 
  Trash2, 
  Copy, 
  Download, 
  Upload,
  Grid,
  Eye
} from "lucide-react";

export default function RoomLayoutDesigner() {
  const [selectedRoom, setSelectedRoom] = useState("OR1");
  const [viewMode, setViewMode] = useState("edit"); // edit, preview
  const [draggedItem, setDraggedItem] = useState(null);
  const roomRef = useRef(null);

  const [roomLayouts, setRoomLayouts] = useState({
    OR1: {
      name: "第1手術室",
      width: 600,
      height: 400,
      equipment: [
        {
          id: "table1",
          name: "手術台",
          type: "surgical-table",
          x: 250,
          y: 150,
          width: 100,
          height: 40,
          rotation: 0,
          isFixed: true,
          model: "マルチ手術台 MT-500"
        },
        {
          id: "anesthesia1",
          name: "麻酔器",
          type: "anesthesia-machine",
          x: 50,
          y: 80,
          width: 60,
          height: 80,
          rotation: 0,
          isFixed: true,
          model: "Perseus A500"
        },
        {
          id: "electrocautery1",
          name: "電気メス",
          type: "electrocautery",
          x: 450,
          y: 100,
          width: 40,
          height: 60,
          rotation: 0,
          isFixed: false,
          model: "ForceTriad"
        }
      ]
    }
  });

  const availableEquipment = [
    { 
      type: "surgical-table", 
      name: "手術台", 
      width: 100, 
      height: 40, 
      color: "#8B4513",
      models: ["マルチ手術台 MT-500", "整形外科用手術台 OT-60", "心臓手術台 CV-9000"]
    },
    { 
      type: "anesthesia-machine", 
      name: "麻酔器", 
      width: 60, 
      height: 80, 
      color: "#4A90E2",
      models: ["Perseus A500", "Primus IE", "Leon MRI"]
    },
    { 
      type: "electrocautery", 
      name: "電気メス", 
      width: 40, 
      height: 60, 
      color: "#E94B3C",
      models: ["ForceTriad", "ERBE VIO 3", "Thunderbeat"]
    },
    { 
      type: "endoscopy-tower", 
      name: "内視鏡タワー", 
      width: 50, 
      height: 120, 
      color: "#50C878",
      models: ["VISERA ELITE III", "IMAGE1 S", "ENDOALPHA 7000"]
    },
    { 
      type: "suction", 
      name: "吸引器", 
      width: 30, 
      height: 50, 
      color: "#FFB347",
      models: ["レビテーター", "ヤンカウアー", "プール吸引"]
    },
    { 
      type: "positioning-device", 
      name: "側臥位固定器", 
      width: 80, 
      height: 30, 
      color: "#DDA0DD",
      models: ["ジェルポジション", "アルファクレードル", "ビーンバッグ"]
    },
    { 
      type: "c-arm", 
      name: "Cアーム", 
      width: 100, 
      height: 80, 
      color: "#20B2AA",
      models: ["OEC 9900 Elite", "Cios Alpha", "Ziehm Vision RFD"]
    },
    { 
      type: "microscope", 
      name: "手術用顕微鏡", 
      width: 60, 
      height: 60, 
      color: "#FF6347",
      models: ["KINEVO 900", "OPMI PENTERO 900", "NC4"]
    }
  ];

  const currentLayout = roomLayouts[selectedRoom];

  const handleDragStart = (e, equipment) => {
    setDraggedItem(equipment);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggedItem || !roomRef.current) return;

    const rect = roomRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left - draggedItem.width / 2, currentLayout.width - draggedItem.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top - draggedItem.height / 2, currentLayout.height - draggedItem.height));

    const newEquipment = {
      id: `${draggedItem.type}_${Date.now()}`,
      name: draggedItem.name,
      type: draggedItem.type,
      x,
      y,
      width: draggedItem.width,
      height: draggedItem.height,
      rotation: 0,
      isFixed: false,
      model: draggedItem.models[0]
    };

    setRoomLayouts({
      ...roomLayouts,
      [selectedRoom]: {
        ...currentLayout,
        equipment: [...currentLayout.equipment, newEquipment]
      }
    });

    setDraggedItem(null);
  };

  const moveEquipment = (equipmentId, deltaX, deltaY) => {
    const equipment = currentLayout.equipment.find(eq => eq.id === equipmentId);
    if (!equipment) return;

    const newX = Math.max(0, Math.min(equipment.x + deltaX, currentLayout.width - equipment.width));
    const newY = Math.max(0, Math.min(equipment.y + deltaY, currentLayout.height - equipment.height));

    setRoomLayouts({
      ...roomLayouts,
      [selectedRoom]: {
        ...currentLayout,
        equipment: currentLayout.equipment.map(eq => 
          eq.id === equipmentId ? { ...eq, x: newX, y: newY } : eq
        )
      }
    });
  };

  const rotateEquipment = (equipmentId) => {
    setRoomLayouts({
      ...roomLayouts,
      [selectedRoom]: {
        ...currentLayout,
        equipment: currentLayout.equipment.map(eq => 
          eq.id === equipmentId ? { 
            ...eq, 
            rotation: (eq.rotation + 90) % 360,
            width: eq.height,
            height: eq.width
          } : eq
        )
      }
    });
  };

  const removeEquipment = (equipmentId) => {
    setRoomLayouts({
      ...roomLayouts,
      [selectedRoom]: {
        ...currentLayout,
        equipment: currentLayout.equipment.filter(eq => eq.id !== equipmentId)
      }
    });
  };

  const getEquipmentIcon = (type) => {
    const icons = {
      "surgical-table": "🛏️",
      "anesthesia-machine": "🏥",
      "electrocautery": "⚡",
      "endoscopy-tower": "📹",
      "suction": "🔄",
      "positioning-device": "📐",
      "c-arm": "📡",
      "microscope": "🔬"
    };
    return icons[type] || "📦";
  };

  const saveLayout = () => {
    const dataStr = JSON.stringify(currentLayout, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${currentLayout.name}_layout.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const renderRoomDesigner = () => (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* 機器パレット */}
      <div className="lg:col-span-1">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">機器パレット</h2>
            <div className="space-y-2">
              {availableEquipment.map((equipment) => (
                <div
                  key={equipment.type}
                  draggable
                  onDragStart={(e) => handleDragStart(e, equipment)}
                  className="flex items-center gap-2 p-2 border rounded cursor-move hover:bg-gray-50"
                >
                  <span className="text-lg">{getEquipmentIcon(equipment.type)}</span>
                  <span className="text-sm font-medium">{equipment.name}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded text-sm text-blue-800">
              💡 機器をドラッグして部屋に配置してください
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 部屋レイアウト */}
      <div className="lg:col-span-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{currentLayout.name} レイアウト設計</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={saveLayout}>
                  <Download className="w-4 h-4 mr-1" />
                  エクスポート
                </Button>
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 mr-1" />
                  インポート
                </Button>
                <Button size="sm">
                  <Save className="w-4 h-4 mr-1" />
                  保存
                </Button>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>部屋サイズ: {currentLayout.width} × {currentLayout.height} px</span>
                <span>配置機器数: {currentLayout.equipment.length}台</span>
              </div>
            </div>
            
            <div 
              ref={roomRef}
              className="relative border-2 border-gray-300 bg-gray-50"
              style={{ 
                width: currentLayout.width, 
                height: currentLayout.height,
                backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* 部屋の境界線 */}
              <div className="absolute inset-0 border-4 border-gray-600"></div>
              
              {/* ドア */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-yellow-500"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-xs text-yellow-700">入口</div>

              {/* 配置された機器 */}
              {currentLayout.equipment.map((equipment) => (
                <div
                  key={equipment.id}
                  className={`absolute cursor-move border-2 rounded shadow-md flex items-center justify-center text-xs font-medium ${
                    equipment.isFixed ? 'border-red-500 bg-red-100' : 'border-blue-500 bg-white hover:bg-blue-50'
                  }`}
                  style={{
                    left: equipment.x,
                    top: equipment.y,
                    width: equipment.width,
                    height: equipment.height,
                    transform: `rotate(${equipment.rotation}deg)`
                  }}
                  onMouseDown={(e) => {
                    if (equipment.isFixed) return;
                    e.preventDefault();
                    const startX = e.clientX - equipment.x;
                    const startY = e.clientY - equipment.y;

                    const handleMouseMove = (e) => {
                      const newX = e.clientX - startX;
                      const newY = e.clientY - startY;
                      moveEquipment(equipment.id, newX - equipment.x, newY - equipment.y);
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                >
                  <div className="text-center">
                    <div className="text-lg">{getEquipmentIcon(equipment.type)}</div>
                    <div className="text-xs truncate w-full">{equipment.name}</div>
                  </div>

                  {/* 操作ボタン */}
                  {!equipment.isFixed && (
                    <div className="absolute -top-8 left-0 flex gap-1 opacity-0 hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          rotateEquipment(equipment.id);
                        }}
                        className="w-6 h-6 bg-blue-500 text-white rounded text-xs flex items-center justify-center"
                      >
                        <RotateCw className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEquipment(equipment.id);
                        }}
                        className="w-6 h-6 bg-red-500 text-white rounded text-xs flex items-center justify-center"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {equipment.isFixed && (
                    <div className="absolute -top-4 left-0 text-xs text-red-600">
                      固定
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 配置機器一覧 */}
            <div className="mt-6">
              <h3 className="font-medium mb-3">配置機器一覧</h3>
              <div className="space-y-2">
                {currentLayout.equipment.map((equipment) => (
                  <div key={equipment.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <span>{getEquipmentIcon(equipment.type)}</span>
                      <div>
                        <div className="font-medium text-sm">{equipment.name}</div>
                        <div className="text-xs text-gray-600">{equipment.model}</div>
                      </div>
                      {equipment.isFixed && (
                        <Badge variant="secondary" className="text-xs">固定</Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      X: {equipment.x}, Y: {equipment.y}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderLayoutPreview = () => (
    <Card>
      <CardContent className="pt-6">
        <h2 className="text-xl font-semibold mb-4">{currentLayout.name} - レイアウト確認</h2>
        <div 
          className="relative border-2 border-gray-300 bg-gray-50 mx-auto"
          style={{ 
            width: currentLayout.width, 
            height: currentLayout.height,
            backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        >
          <div className="absolute inset-0 border-4 border-gray-600"></div>
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-yellow-500"></div>
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-sm text-yellow-700 font-medium">入口</div>

          {currentLayout.equipment.map((equipment) => (
            <div
              key={equipment.id}
              className="absolute border-2 border-gray-400 bg-white rounded shadow-sm flex items-center justify-center text-xs font-medium"
              style={{
                left: equipment.x,
                top: equipment.y,
                width: equipment.width,
                height: equipment.height,
                transform: `rotate(${equipment.rotation}deg)`
              }}
            >
              <div className="text-center">
                <div className="text-lg">{getEquipmentIcon(equipment.type)}</div>
                <div className="text-xs truncate w-full">{equipment.name}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-2">固定機器</h4>
            {currentLayout.equipment.filter(eq => eq.isFixed).map(eq => (
              <div key={eq.id} className="text-sm">• {eq.name} ({eq.model})</div>
            ))}
          </div>
          <div>
            <h4 className="font-medium mb-2">可動機器</h4>
            {currentLayout.equipment.filter(eq => !eq.isFixed).map(eq => (
              <div key={eq.id} className="text-sm">• {eq.name} ({eq.model})</div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">手術室レイアウト設計</h1>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === "edit" ? "default" : "outline"}
            onClick={() => setViewMode("edit")}
            size="sm"
          >
            <Grid className="w-4 h-4 mr-1" />
            編集
          </Button>
          <Button 
            variant={viewMode === "preview" ? "default" : "outline"}
            onClick={() => setViewMode("preview")}
            size="sm"
          >
            <Eye className="w-4 h-4 mr-1" />
            プレビュー
          </Button>
        </div>
      </div>

      <div className="flex gap-4 items-center">
        <label className="font-medium">手術室:</label>
        <select
          value={selectedRoom}
          onChange={(e) => setSelectedRoom(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="OR1">第1手術室</option>
          <option value="OR2">第2手術室</option>
          <option value="OR3">第3手術室</option>
        </select>
      </div>

      {viewMode === "edit" ? renderRoomDesigner() : renderLayoutPreview()}
    </div>
  );
}